import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FaPencilAlt, FaArrowLeft, FaCheck, FaTimes, FaRedo,
  FaLightbulb, FaTrophy, FaBook, FaGraduationCap
} from 'react-icons/fa';
import { getCoachForModule, getRandomQuote, ASSETS } from '../config/schools';
import ShareableBadge from '../components/ShareableBadge';

// Base de données des exercices de style et expressions
const STYLE_EXERCISES = {
  pleonasmes: {
    title: "Pléonasmes à éviter",
    description: "Identifiez les pléonasmes (répétitions inutiles) dans ces phrases",
    icon: "🔄",
    questions: [
      {
        sentence: "Il est monté en haut de la tour.",
        correct: "Il est monté à la tour.",
        explanation: "'Monté' implique déjà 'en haut'. C'est un pléonasme courant.",
        wrongOptions: ["Il est allé en haut de la tour.", "Il est monté au sommet de la tour."]
      },
      {
        sentence: "Je l'ai vu de mes propres yeux.",
        correct: "Je l'ai vu.",
        explanation: "On voit toujours avec ses propres yeux. 'De mes propres yeux' est redondant.",
        wrongOptions: ["Je l'ai aperçu de mes yeux.", "Je l'ai observé de mes propres yeux."]
      },
      {
        sentence: "Il faut prévoir à l'avance.",
        correct: "Il faut prévoir.",
        explanation: "'Prévoir' signifie déjà voir à l'avance.",
        wrongOptions: ["Il faut anticiper à l'avance.", "Il faut planifier à l'avance."]
      },
      {
        sentence: "Ils collaborent ensemble sur ce projet.",
        correct: "Ils collaborent sur ce projet.",
        explanation: "'Collaborer' signifie travailler ensemble.",
        wrongOptions: ["Ils travaillent ensemble sur ce projet.", "Ils coopèrent ensemble sur ce projet."]
      },
      {
        sentence: "C'est un monopole exclusif.",
        correct: "C'est un monopole.",
        explanation: "Un monopole est par définition exclusif.",
        wrongOptions: ["C'est une exclusivité monopolistique.", "C'est un monopole unique."]
      },
      {
        sentence: "Il a ajouté en plus quelques remarques.",
        correct: "Il a ajouté quelques remarques.",
        explanation: "'Ajouter' implique déjà 'en plus'.",
        wrongOptions: ["Il a mis en plus quelques remarques.", "Il a inclus en plus quelques remarques."]
      },
      {
        sentence: "Reculez en arrière s'il vous plaît.",
        correct: "Reculez s'il vous plaît.",
        explanation: "'Reculer' signifie aller en arrière.",
        wrongOptions: ["Allez en arrière s'il vous plaît.", "Repartez en arrière s'il vous plaît."]
      },
      {
        sentence: "C'est une petite maisonnette.",
        correct: "C'est une maisonnette.",
        explanation: "'Maisonnette' signifie déjà petite maison.",
        wrongOptions: ["C'est une petite maison.", "C'est une mini maisonnette."]
      }
    ]
  },
  anglicismes: {
    title: "Anglicismes à remplacer",
    description: "Trouvez l'équivalent français correct",
    icon: "🇬🇧➡️🇫🇷",
    questions: [
      {
        anglicism: "Je vais checker mes emails.",
        correct: "Je vais vérifier mes courriels.",
        explanation: "'Checker' vient de l'anglais 'check'. En français, on utilise 'vérifier' ou 'consulter'.",
        wrongOptions: ["Je vais regarder mes emails.", "Je vais checker mes courriels."]
      },
      {
        anglicism: "On doit se focusser sur ce projet.",
        correct: "On doit se concentrer sur ce projet.",
        explanation: "'Focusser' est un anglicisme. 'Se concentrer' ou 'se focaliser' sont corrects.",
        wrongOptions: ["On doit se focaliser focus sur ce projet.", "On doit focuser sur ce projet."]
      },
      {
        anglicism: "Je suis en charge de ce dossier.",
        correct: "Je suis responsable de ce dossier.",
        explanation: "'Être en charge de' est un calque de 'to be in charge of'. On dit 'être responsable de' ou 'avoir la charge de'.",
        wrongOptions: ["Je suis chargé en chef de ce dossier.", "J'ai en charge ce dossier."]
      },
      {
        anglicism: "C'est définitivement la meilleure option.",
        correct: "C'est assurément la meilleure option.",
        explanation: "'Définitivement' signifie 'de manière définitive' en français, pas 'certainement' comme en anglais.",
        wrongOptions: ["C'est définitive la meilleure option.", "C'est définitif la meilleure option."]
      },
      {
        anglicism: "J'ai eu un feedback positif.",
        correct: "J'ai eu un retour positif.",
        explanation: "'Feedback' se traduit par 'retour', 'commentaire' ou 'avis'.",
        wrongOptions: ["J'ai eu une rétroaction positive.", "J'ai eu un feed positif."]
      },
      {
        anglicism: "On va implémenter cette solution.",
        correct: "On va mettre en œuvre cette solution.",
        explanation: "'Implémenter' est un anglicisme. On utilise 'mettre en œuvre', 'appliquer' ou 'déployer'.",
        wrongOptions: ["On va implementation cette solution.", "On va implanter cette solution."]
      },
      {
        anglicism: "C'est challengeant comme projet.",
        correct: "C'est un projet stimulant.",
        explanation: "'Challengeant' n'existe pas en français. On dit 'stimulant', 'exigeant' ou 'ambitieux'.",
        wrongOptions: ["C'est un projet challenger.", "C'est un projet de challenge."]
      },
      {
        anglicism: "Je vais vous forwarder le mail.",
        correct: "Je vais vous transférer le courriel.",
        explanation: "'Forwarder' se traduit par 'transférer' ou 'faire suivre'.",
        wrongOptions: ["Je vais vous forward le mail.", "Je vais vous envoyer forward le mail."]
      }
    ]
  },
  expressions: {
    title: "Expressions correctes",
    description: "Choisissez la forme correcte de l'expression",
    icon: "💬",
    questions: [
      {
        question: "Quelle est la forme correcte ?",
        options: ["Au temps pour moi", "Autant pour moi"],
        correct: 0,
        explanation: "'Au temps pour moi' est la forme traditionnelle, venant du langage militaire. 'Autant pour moi' est accepté mais moins précis."
      },
      {
        question: "Quelle est la forme correcte ?",
        options: ["Malgré que", "Bien que"],
        correct: 1,
        explanation: "'Malgré que' est incorrect dans la langue soignée. On utilise 'bien que' + subjonctif ou 'malgré' + nom."
      },
      {
        question: "Quelle est la forme correcte ?",
        options: ["Pallier à un problème", "Pallier un problème"],
        correct: 1,
        explanation: "'Pallier' est transitif direct, pas de préposition. On pallie un problème, on ne pallie pas à un problème."
      },
      {
        question: "Quelle est la forme correcte ?",
        options: ["Avoir affaire à", "Avoir à faire à"],
        correct: 0,
        explanation: "'Avoir affaire à' signifie être en relation avec. 'Avoir à faire' signifie devoir accomplir quelque chose."
      },
      {
        question: "Quelle est la forme correcte pour exprimer un avis ?",
        options: ["À mon sens", "À mon avis"],
        correct: 1,
        explanation: "Les deux sont corrects, mais 'à mon avis' est plus courant. 'À mon sens' est plus soutenu."
      },
      {
        question: "Quelle est la forme correcte ?",
        options: ["Faire long feu", "Ne pas faire long feu"],
        correct: 1,
        explanation: "'Faire long feu' signifie échouer. Pour dire que quelque chose ne dure pas, on dit 'ne pas faire long feu'."
      },
      {
        question: "Quelle est la forme correcte ?",
        options: ["Être sur la sellette", "Être sur la sélette"],
        correct: 0,
        explanation: "'Sellette' (petit siège) est la bonne orthographe. L'expression vient du siège où l'accusé s'asseyait."
      },
      {
        question: "Quelle est la forme correcte ?",
        options: ["Aller de paire", "Aller de pair"],
        correct: 1,
        explanation: "'Pair' sans 'e' signifie égal, semblable. 'Paire' désigne un ensemble de deux."
      }
    ]
  },
  journalisme: {
    title: "Style journalistique",
    description: "Maîtrisez les règles d'écriture journalistique",
    icon: "📰",
    questions: [
      {
        question: "Comment doit-on écrire les nombres dans un article ?",
        options: [
          "Toujours en chiffres",
          "En lettres de 0 à 9, en chiffres au-delà",
          "Toujours en lettres",
          "Selon la préférence du journaliste"
        ],
        correct: 1,
        explanation: "Convention journalistique : les nombres de 0 à 9 s'écrivent en lettres, les autres en chiffres."
      },
      {
        question: "Quelle est la règle pour les sigles à la première mention ?",
        options: [
          "Utiliser uniquement le sigle",
          "Écrire la forme développée puis le sigle entre parenthèses",
          "Toujours écrire en toutes lettres",
          "Alterner selon la longueur"
        ],
        correct: 1,
        explanation: "À la première mention, on écrit : Organisation des Nations unies (ONU), puis on utilise ONU."
      },
      {
        question: "Comment cite-t-on une source anonyme ?",
        options: [
          "Une source proche du dossier",
          "Un anonyme bien informé",
          "Selon une source qui a requis l'anonymat",
          "D'après des rumeurs"
        ],
        correct: 2,
        explanation: "On précise que la source a demandé l'anonymat, ce qui renforce la crédibilité."
      },
      {
        question: "Quelle forme de titre est préférable ?",
        options: [
          "Le président a rencontré les syndicats hier",
          "Rencontre hier entre le président et les syndicats",
          "Le président rencontre les syndicats",
          "Hier, rencontre présidentielle avec les syndicats"
        ],
        correct: 2,
        explanation: "Le présent de narration rend le titre plus vivant et dynamique."
      },
      {
        question: "Quel est l'ordre correct dans un chapô ?",
        options: [
          "Contexte, fait principal, conséquences",
          "Fait principal, contexte, enjeux",
          "Chronologie des événements",
          "Citation, explication, analyse"
        ],
        correct: 1,
        explanation: "Un chapô efficace commence par le fait principal, puis le contexte et les enjeux."
      },
      {
        question: "Comment introduit-on une citation ?",
        options: [
          "Selon X, « ... »",
          "X dit que « ... »",
          "« ... », affirme X",
          "Toutes ces formes sont correctes"
        ],
        correct: 3,
        explanation: "Varier les formes d'introduction enrichit l'écriture : verbes introducteurs variés, position variable."
      },
      {
        question: "Quelle est la longueur idéale d'une phrase journalistique ?",
        options: [
          "5-10 mots",
          "15-20 mots",
          "25-30 mots",
          "Plus de 30 mots"
        ],
        correct: 1,
        explanation: "Une phrase de 15-20 mots est idéale : assez courte pour être claire, assez longue pour être informative."
      },
      {
        question: "Que faut-il éviter absolument ?",
        options: [
          "Les phrases courtes",
          "Les adverbes et adjectifs évaluatifs",
          "Les citations",
          "Les chiffres"
        ],
        correct: 1,
        explanation: "Les adverbes ('évidemment', 'malheureusement') et adjectifs évaluatifs trahissent l'opinion du journaliste."
      }
    ]
  }
};

function StyleExpressionsPage() {
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [quizComplete, setQuizComplete] = useState(false);
  const [shuffledOptions, setShuffledOptions] = useState([]);
  
  const coach = getCoachForModule('grammar');

  // Shuffle array function (Fisher-Yates)
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Get current question and shuffle options
  useEffect(() => {
    if (category && STYLE_EXERCISES[category]) {
      const questions = STYLE_EXERCISES[category].questions;
      if (currentIndex < questions.length) {
        const q = questions[currentIndex];
        
        // Prepare options based on question type
        let options = [];
        if (q.options) {
          // Expression type - already has options
          options = q.options.map((opt, idx) => ({ text: opt, isCorrect: idx === q.correct }));
        } else if (q.correct && q.wrongOptions) {
          // Pleonasme/Anglicisme type
          options = [
            { text: q.correct, isCorrect: true },
            ...q.wrongOptions.map(opt => ({ text: opt, isCorrect: false }))
          ];
        }
        
        setShuffledOptions(shuffleArray(options));
      }
    }
  }, [category, currentIndex]);

  const handleAnswer = (option) => {
    if (showResult) return;
    
    setSelectedAnswer(option);
    setShowResult(true);
    
    if (option.isCorrect) {
      setScore(score + 1);
    }
    setShowExplanation(true);
  };

  const nextQuestion = () => {
    const questions = STYLE_EXERCISES[category].questions;
    
    if (currentIndex + 1 >= questions.length) {
      setQuizComplete(true);
    } else {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setShowResult(false);
      setShowExplanation(false);
    }
  };

  const resetQuiz = () => {
    setCurrentIndex(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setShowExplanation(false);
    setQuizComplete(false);
    setCategory(null);
  };

  // Category selection screen
  if (!category) {
    return (
      <div className="min-h-screen p-4" style={{ background: '#F8F4E3' }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="comic-button flex items-center"
              data-testid="back-button"
            >
              <FaArrowLeft className="mr-2" /> Retour
            </button>
          </div>

          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-center mb-8"
          >
            <h1 className="comic-title text-3xl md:text-4xl mb-4" style={{ color: '#E63946' }}>
              <FaPencilAlt className="inline mr-2" /> Style & Expressions
            </h1>
            <p className="text-lg text-gray-600">
              Perfectionnez votre écriture journalistique
            </p>
          </motion.div>

          {/* Coach */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="flex items-center justify-center mb-8"
          >
            <div className="comic-panel p-4 flex items-center space-x-4 max-w-md">
              <img src={coach.image || ASSETS.SUPER_REPORTER} alt={coach.name} className="w-16 h-16 rounded-full object-cover" />
              <div>
                <p className="font-bold">{coach.name}</p>
                <p className="text-sm italic">"{getRandomQuote(coach)}"</p>
              </div>
            </div>
          </motion.div>

          {/* Categories */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(STYLE_EXERCISES).map(([key, cat], index) => (
              <motion.div
                key={key}
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => setCategory(key)}
                className="comic-panel cursor-pointer hover:scale-105 transition-transform"
                data-testid={`category-${key}`}
              >
                <div className="text-4xl mb-3">{cat.icon}</div>
                <h3 className="comic-text text-xl mb-2">{cat.title}</h3>
                <p className="text-sm text-gray-600 mb-3">{cat.description}</p>
                <p className="text-xs text-blue-600">{cat.questions.length} exercices</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const currentCategory = STYLE_EXERCISES[category];
  const currentQuestion = currentCategory.questions[currentIndex];
  const totalQuestions = currentCategory.questions.length;
  const percentage = Math.round((score / totalQuestions) * 100);

  // Quiz complete screen
  if (quizComplete) {
    const passed = percentage >= 70;
    
    return (
      <div className="min-h-screen p-4 flex items-center justify-center" style={{ background: '#F8F4E3' }}>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="comic-panel max-w-md w-full text-center"
        >
          <div className="text-6xl mb-4">
            {passed ? '🎉' : '📚'}
          </div>
          <h2 className="comic-title text-2xl mb-4">
            {passed ? 'Excellent !' : 'Continue à t\'entraîner !'}
          </h2>
          
          <div className="mb-6">
            <div className="text-5xl font-bold mb-2" style={{ color: passed ? '#10B981' : '#E63946' }}>
              {score}/{totalQuestions}
            </div>
            <p className="text-gray-600">{percentage}% de bonnes réponses</p>
          </div>

          {passed && (
            <div className="mb-6">
              <ShareableBadge
                courseName={`Style & Expressions - ${currentCategory.title}`}
                score={percentage}
                badgeColor="#E63946"
              />
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={() => {
                setCategory(null);
                setCurrentIndex(0);
                setScore(0);
                setQuizComplete(false);
              }}
              className="comic-button comic-button-navy w-full"
              data-testid="try-another"
            >
              <FaBook className="inline mr-2" /> Autre catégorie
            </button>
            <button
              onClick={() => {
                setCurrentIndex(0);
                setScore(0);
                setQuizComplete(false);
                setSelectedAnswer(null);
                setShowResult(false);
              }}
              className="comic-button w-full"
              data-testid="retry-quiz"
            >
              <FaRedo className="inline mr-2" /> Réessayer
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="comic-button bg-gray-200 w-full"
            >
              <FaArrowLeft className="inline mr-2" /> Retour au dashboard
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Quiz screen
  return (
    <div className="min-h-screen p-4" style={{ background: '#F8F4E3' }}>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => setCategory(null)}
            className="comic-button flex items-center"
          >
            <FaArrowLeft className="mr-2" /> Catégories
          </button>
          <div className="text-right">
            <div className="comic-text text-lg">{currentCategory.icon} {currentCategory.title}</div>
            <div className="text-sm text-gray-600">Question {currentIndex + 1}/{totalQuestions}</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-3 mb-6">
          <motion.div
            className="h-3 rounded-full"
            style={{ background: '#E63946' }}
            initial={{ width: 0 }}
            animate={{ width: `${((currentIndex + 1) / totalQuestions) * 100}%` }}
          />
        </div>

        {/* Score */}
        <div className="text-center mb-6">
          <span className="comic-panel inline-block px-4 py-2">
            <FaTrophy className="inline mr-2 text-yellow-500" />
            Score: {score}/{currentIndex + (showResult ? 1 : 0)}
          </span>
        </div>

        {/* Question */}
        <motion.div
          key={currentIndex}
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          className="comic-panel mb-6"
        >
          <div className="mb-4">
            {currentQuestion.sentence && (
              <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg mb-4">
                <p className="text-lg font-medium text-red-800">
                  <FaTimes className="inline mr-2 text-red-500" />
                  {currentQuestion.sentence || currentQuestion.anglicism}
                </p>
              </div>
            )}
            {currentQuestion.anglicism && (
              <div className="p-4 bg-orange-50 border-2 border-orange-200 rounded-lg mb-4">
                <p className="text-lg font-medium text-orange-800">
                  🇬🇧 {currentQuestion.anglicism}
                </p>
              </div>
            )}
            {currentQuestion.question && (
              <p className="text-xl font-bold mb-4">{currentQuestion.question}</p>
            )}
            
            <p className="text-gray-600">
              {category === 'pleonasmes' && "Quelle formulation est correcte ?"}
              {category === 'anglicismes' && "Quelle est la traduction française correcte ?"}
              {category === 'expressions' && "Sélectionnez la bonne réponse"}
              {category === 'journalisme' && "Sélectionnez la bonne réponse"}
            </p>
          </div>

          {/* Options */}
          <div className="space-y-3">
            {shuffledOptions.map((option, idx) => {
              let bgColor = 'bg-white hover:bg-gray-50';
              let borderColor = 'border-gray-300';
              
              if (showResult) {
                if (option.isCorrect) {
                  bgColor = 'bg-green-100';
                  borderColor = 'border-green-500';
                } else if (selectedAnswer === option && !option.isCorrect) {
                  bgColor = 'bg-red-100';
                  borderColor = 'border-red-500';
                }
              }
              
              return (
                <motion.button
                  key={idx}
                  onClick={() => handleAnswer(option)}
                  disabled={showResult}
                  className={`w-full p-4 text-left rounded-lg border-2 transition-all ${bgColor} ${borderColor}`}
                  whileHover={!showResult ? { scale: 1.02 } : {}}
                  data-testid={`option-${idx}`}
                >
                  <div className="flex items-center justify-between">
                    <span>{option.text}</span>
                    {showResult && option.isCorrect && (
                      <FaCheck className="text-green-500" />
                    )}
                    {showResult && selectedAnswer === option && !option.isCorrect && (
                      <FaTimes className="text-red-500" />
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </motion.div>

        {/* Explanation */}
        <AnimatePresence>
          {showExplanation && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="comic-panel mb-6 bg-blue-50"
            >
              <div className="flex items-start space-x-3">
                <FaLightbulb className="text-2xl text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-bold mb-1">Explication</p>
                  <p className="text-gray-700">{currentQuestion.explanation}</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Next button */}
        {showResult && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-center"
          >
            <button
              onClick={nextQuestion}
              className="comic-button comic-button-navy px-8 py-3"
              data-testid="next-question"
            >
              {currentIndex + 1 >= totalQuestions ? 'Voir les résultats' : 'Question suivante'}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default StyleExpressionsPage;
